import { boolean, index, jsonb, pgTable, real, text, timestamp, uuid, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

// =============================================================================
// COMPANY TARGETS — the §H CompanyDiscoveryService's output table (H-1a).
// docs/briefs/launch-week-briefs.md §H; the worked fixture this table's shape
// reproduces: docs/design/target-companies-2026-07-02.md (companies + cited
// signals + sponsorship reads + fit notes + honesty flags — what Fable did by
// hand, this table is the repeatable service's landing surface for).
//
// Per-user (userId, text — matches the codebase's prevailing text-keyed
// user_id convention on jobs/watchlist_sources/search_scenarios; the uuid
// migration tracked in CLAUDE.md is a later, separate pass, not this table's
// job to pre-empt). A CompanyDiscoveryService run inserts 'suggested' rows;
// H-1b (LATER) reviews them; H-1c (LATER) wires accept -> ATS watchlist +
// dream bucket. This table is built so those bolt on WITHOUT a reshape:
// status is already the three-state field H-1c's accept/dismiss actions flip,
// sourceRefs is already the provenance H-1c's ATS-seed handoff reads, and
// signals is already shaped for H-1b's per-signal chip rendering.
//
// NO INVENTION, enforced in CODE (not just documented here): every element of
// `signals` MUST carry a sourceUrl — CompanyDiscoveryService rejects a
// candidate signal at the boundary if it doesn't (see
// apps/api/lib/companyDiscovery.ts, assertSignalsGrounded). This column's
// jsonb shape does not itself enforce the constraint (Postgres CHECK
// constraints over jsonb array element shape are impractical here, same
// reasoning grounding_gaps.ts and every other NO-INVENTION jsonb column in
// this codebase already accepts) — the gate lives in the service, tested
// directly (company-targets grounding rejection tests).
// =============================================================================

/** One cited signal behind a company's candidacy. `sourceUrl` is REQUIRED at
 *  the type level (not optional) so a caller cannot even construct an
 *  ungrounded signal without lying to the type system — the runtime gate in
 *  companyDiscovery.ts is the real enforcement (a Claude response is
 *  untyped JSON), but the type still documents the invariant. */
export interface CompanyTargetSignal {
  /** The claim itself — e.g. "Named 'Senior AI Architect (Perth)' req live
   *  during research" or "Telstra-Accenture $700M AI/data JV". */
  claim: string;
  /** The public source the claim traces to. NEVER absent — a signal without
   *  one is rejected before it reaches this table (NO INVENTION, in code). */
  sourceUrl: string;
  /** Optional page/article title, for UI display. */
  title?: string;
  /** ISO timestamp the signal was retrieved/verified, when known. */
  retrievedAt?: string;
}

/** A source reference backing the row as a whole (distinct from per-signal
 *  sourceUrl — this is the broader research trail: the careers-page fetch
 *  that verified the company resolves, the sector-list source, etc.). Same
 *  shape as LakeSource (_shared.ts) but kept local: company_targets is a
 *  per-user output, not a shared lake pond. */
export interface CompanyTargetSourceRef {
  url: string;
  kind?: 'careers_page_verification' | 'sector_source' | 'research_citation' | 'other';
  title?: string;
  retrievedAt?: string;
}

/** Tri-state visa-sponsorship read for this company — the SAME shape
 *  sponsorshipScanner.ts's SponsorshipSignal already establishes for a job
 *  listing's own text (explicit_yes | explicit_no | silent). Reused verbatim
 *  rather than inventing a fourth state: 'silent' already IS "undetermined,
 *  never guessed" in this codebase's vocabulary. Nullable at the column level
 *  for geos where sponsorship isn't even a relevant axis (e.g. a US-citizen
 *  targeting US-remote roles) — null over a forced guess.
 */
export type CompanyTargetSponsorshipRead = 'explicit_yes' | 'explicit_no' | 'silent';

export type CompanyTargetStatus = 'suggested' | 'accepted' | 'dismissed';

export const companyTargets = pgTable(
  'company_targets',
  {
    id: id(),
    userId: text('user_id').notNull(),
    company: varchar('company', { length: 200 }).notNull(),
    /** Free-text sector label the discovery run was scoped to (e.g.
     *  "AU mining", "AI-native US"). Sector PACKS (H-1d, LATER) will turn this
     *  into a lookup against a data table; today it is the literal input
     *  `sector` string the run was called with, stored for provenance. */
    sector: varchar('sector', { length: 120 }).notNull(),
    /** Free-text geo label the discovery run was scoped to (e.g. "AU", "US
     *  remote", "Perth, WA"). Same provenance-not-lookup shape as `sector`
     *  today. */
    geo: varchar('geo', { length: 120 }).notNull(),
    /** 0-100. Null when the engine could not produce a defensible ranking for
     *  this candidate (undetermined, never a fabricated default). */
    fitScore: real('fit_score'),
    /** CompanyTargetSignal[] — every entry MUST carry sourceUrl (enforced in
     *  companyDiscovery.ts before insert, never trusted from the jsonb shape
     *  alone). Defaults to an empty array, never null, so callers can always
     *  .length / .map without a null check. */
    signals: jsonb('signals').$type<CompanyTargetSignal[]>().default([]).notNull(),
    /** Tri-state, nullable — see CompanyTargetSponsorshipRead. Null = not
     *  evaluated / not applicable for this geo, never a guessed no. */
    sponsorshipRead: varchar('sponsorship_read', { length: 16 }).$type<
      CompanyTargetSponsorshipRead | null
    >(),
    /** Free-text note accompanying the tri-state read (e.g. the fixture's own
     *  "mining sponsorship skews to trades/ops" honesty flag). Null when
     *  sponsorshipRead is null. */
    sponsorshipNote: text('sponsorship_note'),
    /** Row-level provenance trail — the careers-page verification fetch, the
     *  sector-list source, etc. Same nullable-array-default shape as
     *  `signals`. */
    sourceRefs: jsonb('source_refs').$type<CompanyTargetSourceRef[]>().default([]).notNull(),
    /** suggested (CompanyDiscoveryService's own output) -> accepted | dismissed
     *  (H-1b, human review, LATER). dismissed is TERMINAL: a re-run of the
     *  discovery service must never resurrect or duplicate a dismissed
     *  company for this user (companyDiscovery.ts's dedup check reads this
     *  column). Enforced at the application layer (Postgres has no
     *  once-dismissed-always-dismissed constraint primitive); tested directly. */
    status: varchar('status', { length: 16 }).$type<CompanyTargetStatus>().default('suggested').notNull(),
    /** Free-text fit note — the engine's why-this-company reasoning, grounded
     *  in the same signals array (never a separate invented narrative). */
    fitNote: text('fit_note'),
    /** Which discovery run produced this row (a client-generated UUID shared
     *  across every row from one CompanyDiscoveryService call) — lets a
     *  caller group "everything from the AU-mining run I kicked off just
     *  now" without a separate runs table (H-1a is intentionally the
     *  smallest table that still supports that; a dedicated
     *  discovery_runs audit table is a LATER addition if the grouping need
     *  grows beyond this column). */
    runId: uuid('run_id'),
    /** When a URL-verification fetch (the atsAutoDiscovery-style
     *  claimOnceForDomain check) last confirmed this company's careers/about
     *  URL resolves. Null until the verification stage has run at least once
     *  for this row. */
    verifiedAt: timestamp('verified_at', { withTimezone: true }),
    /** D-2 test-data flag (docs/BUILD-PLAN-2026-07-04.md, Reserved call 1):
     *  stamped `true` only when the OWNING user carried
     *  `account_kind IN ('demo','qa')` at the moment CompanyDiscoveryService
     *  inserted this row (threaded from the caller's userId lookup, never
     *  re-derived inside companyDiscovery.ts). Defaults `false`. Same
     *  parity-with-the-other-4-tables posture as watchlistSources.isTestData
     *  above — company_targets is already per-user-scoped on every existing
     *  read, so this is primarily audit/consistency plus future-proofing any
     *  shared/admin read this table ever grows. */
    isTestData: boolean('is_test_data').notNull().default(false),
    ...timestamps,
  },
  (t) => ({
    userIdx: index('company_targets_user_idx').on(t.userId),
    userCompanyIdx: index('company_targets_user_company_idx').on(t.userId, t.company),
    userStatusIdx: index('company_targets_user_status_idx').on(t.userId, t.status),
    runIdx: index('company_targets_run_idx').on(t.runId),
  }),
);
